import { useState, useCallback } from "react";
import { api } from "../services/api";
import { useAuth } from "../global-components/src";

interface UseRewritePostOptions {
    type?: "post" | "poem" | "ads";
    onSuccess: (text: string) => void;
    getText: () => string;
}

export function useRewritePost({ type = "post", onSuccess, getText }: UseRewritePostOptions) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { token } = useAuth();
    // history[0] = original, history[1] = reescrito, etc.
    const [history, setHistory] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    const rewrite = useCallback(async () => {
        const currentText = getText();
        if (!currentText.trim()) return;

        setLoading(true);
        setError(null);

        try {
            const response = await api(token).post("/ai/rewrite-post", { text: currentText, type });

            const data = await response.data;

            // Salva estado atual no histórico antes de reescrever
            setHistory(prev => {
                const base = historyIndex === -1 ? [] : prev.slice(0, historyIndex + 1);
                // Se ainda não tem o original, adiciona ele primeiro
                const withOriginal = base.length === 0 ? [currentText, data.text] : [...base, data.text];
                return withOriginal;
            });

            setHistoryIndex(prev => (prev === -1 ? 1 : prev + 1));
            onSuccess(data.text);
        } catch (e) {
            console.error(e);
            
            setError("Erro de conexão.");
        } finally {
            setLoading(false);
        }
    }, [getText, type, historyIndex, onSuccess]);

    const undo = useCallback(() => {
        if (!canUndo) return;
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        onSuccess(history[newIndex]);
    }, [canUndo, historyIndex, history, onSuccess]);

    const redo = useCallback(() => {
        if (!canRedo) return;
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        onSuccess(history[newIndex]);
    }, [canRedo, historyIndex, history, onSuccess]);

    const reset = useCallback(() => {
        setHistory([]);
        setHistoryIndex(-1);
        setError(null);
    }, []);

    return { rewrite, undo, redo, canUndo, canRedo, loading, error, reset };
}